import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLogin, useRedirect } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  forgotPassword,
  confirmForgotPassword,
  completeNewPassword,
  clearNewPasswordChallenge,
  beginSoftwareTokenSetup,
  completeSoftwareTokenMfa,
  selectSoftwareMfa,
} from '../providers/cognito';
import { mapCognitoError, POLICY_NOTE, type LoginView as View } from '../providers/cognito-errors';

export { mapCognitoError };

export const LoginPage = () => {
  const login = useLogin();
  const redirect = useRedirect();

  const viewGeneration = useRef(0);
  useEffect(() => () => {
    viewGeneration.current++;
    clearNewPasswordChallenge();
  }, []);
  const [setupSecret, setSetupSecret] = useState('');
  const [view, setView] = useState<View>('signin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (loading) return;
    const target = errorMsg
      ? pageRef.current?.querySelector<HTMLElement>('#signin-error')
      : pageRef.current?.querySelector<HTMLElement>('form input, form button:not(:disabled), [data-success-heading]');
    target?.focus();
  }, [view, errorMsg, setupSecret, loading]);

  const goTo = (next: View) => {
    setErrorMsg('');
    setView(next);
  };

  const routeChallenge = (err: unknown): boolean => {
    const code = (err as { code?: string })?.code;
    const destination: Record<string, View> = {
      NEW_PASSWORD_REQUIRED: 'new-password', SOFTWARE_TOKEN_MFA: 'mfa', MFA_SETUP: 'mfa-setup', SELECT_SOFTWARE_MFA: 'mfa-select',
    };
    if (!code || !destination[code]) return false;
    setCode(''); setSetupSecret(''); setPassword(''); setNewPassword(''); setConfirmPassword('');
    goTo(destination[code]);
    return true;
  };

  const exitTerminalChallenge = (err: unknown, errorView: View): boolean => {
    const challenge = err as { code?: string; name?: string; message?: string };
    const errorCode = challenge.code ?? challenge.name;
    if (!['CHALLENGE_EXPIRED', 'ExpiredCodeException', 'NotAuthorizedException'].includes(
      errorCode ?? '',
    )) {
      return false;
    }

    const message = mapCognitoError(challenge, errorView);
    // The provider clears only the terminal challenge that produced this
    // error. Do not issue a second global clear here: a stale UI promise must
    // not cancel a newer sign-in generation.
    viewGeneration.current++;
    setSetupSecret('');
    setPassword('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setView('signin');
    setErrorMsg(message);
    setLoading(false);
    return true;
  };

  const handleMfa = async (event: FormEvent) => {
    const operationGeneration = viewGeneration.current;
    event.preventDefault(); setLoading(true); setErrorMsg('');
    try {
      if (view === 'mfa-select') await selectSoftwareMfa();
      else await completeSoftwareTokenMfa(code);
      if (operationGeneration !== viewGeneration.current) return;
      setSetupSecret(''); setCode(''); redirect('/');
    } catch (err) {
      if (operationGeneration !== viewGeneration.current) return;
      if (!exitTerminalChallenge(err, view) && !routeChallenge(err)) {
        setErrorMsg(mapCognitoError(err as Error, view));
      }
    } finally {
      if (operationGeneration === viewGeneration.current) setLoading(false);
    }
  };
  const handleSetup = async () => {
    const operationGeneration = viewGeneration.current;
    setLoading(true); setErrorMsg('');
    try {
      const secret = await beginSoftwareTokenSetup();
      if (operationGeneration !== viewGeneration.current) return;
      setSetupSecret(secret);
    }
    catch (err) {
      if (operationGeneration !== viewGeneration.current) return;
      if (!exitTerminalChallenge(err, 'mfa-setup')) {
        setErrorMsg(mapCognitoError(err as Error, 'mfa-setup'));
      }
    }
    finally {
      if (operationGeneration === viewGeneration.current) setLoading(false);
    }
  };

  const handleSignIn = async (e: FormEvent) => {
    const operationGeneration = ++viewGeneration.current;
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);
    try {
      await login({ username, password });
    } catch (err: unknown) {
      if (operationGeneration !== viewGeneration.current) return;
      const e = err as { code?: string; name?: string; message?: string };
      if (!routeChallenge(e)) {
        setErrorMsg(mapCognitoError(e, 'signin'));
      }
    } finally {
      if (operationGeneration === viewGeneration.current) setLoading(false);
    }
  };

  const handleNewPassword = async (e: FormEvent) => {
    const operationGeneration = viewGeneration.current;
    e.preventDefault();
    setErrorMsg('');
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await completeNewPassword(newPassword);
      if (operationGeneration !== viewGeneration.current) return;
      redirect('/');
    } catch (err: unknown) {
      if (operationGeneration !== viewGeneration.current) return;
      if (!exitTerminalChallenge(err, 'new-password') && !routeChallenge(err)) {
        setErrorMsg(mapCognitoError(
          err as { code?: string; name?: string; message?: string },
          'new-password',
        ));
      }
    } finally {
      if (operationGeneration === viewGeneration.current) setLoading(false);
    }
  };

  const handleForgotA = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);
    try {
      await forgotPassword(username);
      goTo('forgot-b');
    } catch (err: unknown) {
      const e = err as { code?: string; name?: string; message?: string };
      if (e?.code === 'UserNotFoundException' || e?.name === 'UserNotFoundException') {
        goTo('forgot-b');
      } else {
        setErrorMsg(mapCognitoError(e, 'forgot-a'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotB = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await confirmForgotPassword(username, code, newPassword);
      goTo('success');
    } catch (err: unknown) {
      setErrorMsg(mapCognitoError(err as { code?: string; name?: string; message?: string }, 'forgot-b'));
    } finally {
      setLoading(false);
    }
  };

  const resetAndGoSignIn = () => {
    viewGeneration.current++;
    clearNewPasswordChallenge();
    setSetupSecret('');
    setPassword('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setLoading(false);
    goTo('signin');
  };

  return (
    <Box
      component="main"
      ref={pageRef}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F4F6F8',
        '& button, & a': { minHeight: 44, minWidth: 44 },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#6C757D' },
        '& a': { display: 'inline-flex', alignItems: 'center' },
        '& :focus-visible': { outline: '3px solid #005FCC', outlineOffset: '3px' },
      }}
    >
      <Paper sx={{ p: 4, width: '100%', maxWidth: 400 }} elevation={2}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography component="h1" variant="h4" fontWeight={700} color="primary" letterSpacing="-0.02em">
            CHAMBER
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Human Performance Assessment Platform
          </Typography>
        </Box>

        <Typography role="status" aria-live="polite" aria-atomic="true" variant="body2">
          {loading ? 'Please wait. Sign-in request in progress.' : ''}
        </Typography>
        {view === 'signin' && (
          <Box component="form" onSubmit={handleSignIn} noValidate>
            <Typography component="h2" variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Sign in
            </Typography>
            {errorMsg && (
              <Alert id="signin-error" tabIndex={-1} severity="error" sx={{ mb: 2 }} role="alert">
                {errorMsg}
              </Alert>
            )}
            <TextField
              id="signin-username"
              label="Username"
              name="username"
              autoComplete="username"
              fullWidth
              required
              inputProps={errorMsg ? { 'aria-describedby': 'signin-error' } : {}}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              id="signin-password"
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              fullWidth
              required
              inputProps={errorMsg ? { 'aria-describedby': 'signin-error' } : {}}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mb: 1.5 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" aria-label="Signing in" /> : 'Sign in'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => goTo('forgot-a')}
                underline="hover"
              >
                Forgot password?
              </Link>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" align="center">
              Need help?{' '}
              <Link href="mailto:Support@orchestramacro.com" underline="hover">
                Contact support
              </Link>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" align="center">
              <Link href="#/redeem" underline="hover">
                Have an activation code?
              </Link>
            </Typography>
          </Box>
        )}

        {(view === 'mfa' || view === 'mfa-setup' || view === 'mfa-select') && (
          <Box component="form" onSubmit={handleMfa} noValidate>
            <Typography component="h2" variant="h6" sx={{ mb: 1 }}>{view === 'mfa-setup' ? 'Set up your authenticator' : 'Verify your sign-in'}</Typography>
            <Typography sx={{ mb: 2 }}>Account: {username}</Typography>
            {errorMsg && <Alert id="signin-error" tabIndex={-1} severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
            {view === 'mfa-select' ? <Button type="submit" variant="contained" disabled={loading}>Use authenticator app</Button> : <>
              {view === 'mfa-setup' && <>
                <Typography sx={{ mb: 2 }}>Add a time-based account in your approved authenticator app. The setup key appears only during this sign-in.</Typography>
                {!setupSecret ? <Button type="button" onClick={handleSetup} disabled={loading}>Show setup key</Button> :
                  <Box sx={{ mb: 2 }}><Typography variant="caption">Setup key — keep private</Typography><Typography aria-label="Authenticator setup key" component="code" sx={{ display: 'block', overflowWrap: 'anywhere' }}>{setupSecret}</Typography></Box>}
              </>}
              {(view === 'mfa' || setupSecret) && <>
                <TextField id="authenticator-code" fullWidth label="Authenticator code" value={code} onChange={event => setCode(event.target.value)}
                  autoComplete="one-time-code" inputProps={{ 'aria-describedby': errorMsg ? 'signin-error' : undefined, 'aria-invalid': !!errorMsg, inputMode: 'numeric', maxLength: 6 }} sx={{ my: 2 }} />
                <Button type="submit" variant="contained" fullWidth disabled={loading}>{loading ? 'Verifying…' : 'Verify code'}</Button>
              </>}
            </>}
            <Button type="button" fullWidth onClick={resetAndGoSignIn} sx={{ mt: 2 }}>Back to sign in</Button>
          </Box>
        )}

        {view === 'forgot-a' && (
          <Box component="form" onSubmit={handleForgotA} noValidate>
            <Typography component="h2" variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Reset your password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your username and we'll send a verification code to the email address on file.
            </Typography>
            {errorMsg && (
              <Alert id="signin-error" tabIndex={-1} severity="error" sx={{ mb: 2 }} role="alert">
                {errorMsg}
              </Alert>
            )}
            <TextField
              id="forgot-username"
              label="Username"
              name="username"
              autoComplete="username"
              fullWidth
              required
              inputProps={errorMsg ? { 'aria-describedby': 'signin-error' } : {}}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mb: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" aria-label="Sending code" />
              ) : (
                'Send verification code'
              )}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => goTo('signin')}
                underline="hover"
              >
                Back to sign in
              </Link>
            </Box>
          </Box>
        )}

        {view === 'forgot-b' && (
          <Box component="form" onSubmit={handleForgotB} noValidate>
            <Typography component="h2" variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Set a new password
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              If that account exists, a verification code has been sent to the email address on file.
            </Alert>
            {errorMsg && (
              <Alert id="signin-error" tabIndex={-1} severity="error" sx={{ mb: 2 }} role="alert">
                {errorMsg}
              </Alert>
            )}
            <TextField
              id="verification-code"
              label="Verification code"
              name="code"
              autoComplete="one-time-code"
              fullWidth
              required
              inputProps={errorMsg ? { 'aria-describedby': 'signin-error' } : {}}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              id="new-password"
              label="New password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              fullWidth
              required
              inputProps={errorMsg ? { 'aria-describedby': 'signin-error new-password-helper-text' } : {}}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText={POLICY_NOTE}
              sx={{ mb: 2 }}
            />
            <TextField
              id="confirm-password"
              label="Confirm new password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              fullWidth
              required
              inputProps={errorMsg ? { 'aria-describedby': 'signin-error' } : {}}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mb: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" aria-label="Setting password" />
              ) : (
                'Set new password'
              )}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => goTo('forgot-a')}
                underline="hover"
              >
                Didn't receive a code? Try again
              </Link>
            </Box>
          </Box>
        )}

        {view === 'new-password' && (
          <Box component="form" onSubmit={handleNewPassword} noValidate>
            <Typography component="h2" variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Set your new password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your administrator set a temporary password. Please create a permanent one to continue.
            </Typography>
            {errorMsg && (
              <Alert id="signin-error" tabIndex={-1} severity="error" sx={{ mb: 2 }} role="alert">
                {errorMsg}
              </Alert>
            )}
            <TextField
              id="set-new-password"
              label="New password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              fullWidth
              required
              inputProps={errorMsg ? { 'aria-describedby': 'signin-error set-new-password-helper-text' } : {}}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText={POLICY_NOTE}
              sx={{ mb: 2 }}
            />
            <TextField
              id="set-confirm-password"
              label="Confirm new password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              fullWidth
              required
              inputProps={errorMsg ? { 'aria-describedby': 'signin-error' } : {}}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mb: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" aria-label="Setting password" />
              ) : (
                'Set password and sign in'
              )}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  clearNewPasswordChallenge();
                  setNewPassword('');
                  setConfirmPassword('');
                  goTo('signin');
                }}
                underline="hover"
              >
                Back to sign in
              </Link>
            </Box>
          </Box>
        )}

        {view === 'success' && (
          <Box>
            <Alert data-success-heading tabIndex={-1} severity="success" sx={{ mb: 2 }}>
              Your password has been reset successfully.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can now sign in with your new password.
            </Typography>
            <Button variant="contained" fullWidth onClick={resetAndGoSignIn}>
              Back to sign in
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
